import { Empty, Input, Segmented, Spin, message as antdMessage } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { ContactAvatar } from "../components/ContactAvatar";
import { PageHeader } from "../components/PageHeader";
import { listContacts, setContactTier } from "../api/contacts";
import type { Contact, ContactTier } from "../api/contacts";
import { useIsMobile } from "../hooks/useBreakpoint";

type TierFilter = "all" | ContactTier;

const TIER_LABEL: Record<ContactTier, string> = {
  important: "重要",
  normal: "普通",
  unimportant: "不重要",
};

const TIER_HINT: Record<ContactTier, string> = {
  important: "实时推送通知,人工审核",
  normal: "不打扰,可手动让 AI 起草",
  unimportant: "AI 自动起草回复",
};

const TIER_ACCENT: Record<ContactTier, string> = {
  important: "#5B6CFF",
  normal: "#94A3B8",
  unimportant: "#FF9F43",
};

export function ContactsPage() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState<TierFilter>("all");
  const [q, setQ] = useState("");
  const isMobile = useIsMobile();

  const refresh = async (t: TierFilter, query: string) => {
    setLoading(true);
    try {
      const { contacts } = await listContacts(t, query.trim() || undefined);
      setContacts(contacts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      void refresh(tier, q);
    }, 200);
    return () => clearTimeout(handle);
  }, [tier, q]);

  const onChangeTier = async (contact: Contact, newTier: ContactTier) => {
    if (newTier === contact.tier) return;
    setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, tier: newTier } : c)));
    try {
      await setContactTier(contact.id, newTier);
    } catch {
      antdMessage.error("更新失败");
      setContacts((cs) => cs.map((c) => (c.id === contact.id ? { ...c, tier: contact.tier } : c)));
    }
  };

  const importantCount = contacts.filter((c) => c.tier === "important").length;
  const unimportantCount = contacts.filter((c) => c.tier === "unimportant").length;

  return (
    <>
      <PageHeader
        title="联系人"
        subtitle={`共 ${contacts.length} 人 · ${importantCount} 重要 · ${unimportantCount} 不重要(AI 自动处理)`}
      />
      <div style={{ padding: 16, maxWidth: 880, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Segmented<TierFilter>
            value={tier}
            onChange={(v) => setTier(v as TierFilter)}
            options={[
              { label: "全部", value: "all" },
              { label: "重要", value: "important" },
              { label: "普通", value: "normal" },
              { label: "不重要", value: "unimportant" },
            ]}
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜索邮箱或姓名"
            prefix={<SearchOutlined style={{ color: "var(--ant-color-text-secondary)" }} />}
            style={{ maxWidth: 320, flex: 1 }}
            allowClear
          />
        </div>

        {loading && contacts.length === 0 ? (
          <div style={{ padding: 32, display: "grid", placeItems: "center" }}>
            <Spin />
          </div>
        ) : contacts.length === 0 ? (
          <Empty description="还没有联系人,先去 Inbox 同步邮件" />
        ) : (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "grid",
              gap: 8,
              gridTemplateColumns: "minmax(0, 1fr)",
            }}
          >
            {contacts.map((c) => (
              <li
                key={c.id}
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "stretch" : "center",
                  gap: 12,
                  padding: "12px 16px",
                  background: "var(--ant-color-bg-container)",
                  border: "1px solid var(--ant-color-border)",
                  borderLeft: `4px solid ${TIER_ACCENT[c.tier]}`,
                  borderRadius: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
                  <ContactAvatar
                    email={c.email}
                    name={c.name}
                    tier={c.tier}
                    size={40}
                    interactive={false}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.name || c.email}
                    </div>
                    {c.name ? (
                      <div
                        style={{
                          fontSize: 14,
                          color: "var(--ant-color-text-secondary)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.email}
                      </div>
                    ) : null}
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--ant-color-text-secondary)",
                        marginTop: 2,
                      }}
                    >
                      {TIER_HINT[c.tier]}
                    </div>
                  </div>
                </div>
                <Segmented<ContactTier>
                  className={`tier-pill tier-pill-${c.tier}`}
                  value={c.tier}
                  onChange={(v) => onChangeTier(c, v as ContactTier)}
                  size="small"
                  options={(["important", "normal", "unimportant"] as ContactTier[]).map((t) => ({
                    label: TIER_LABEL[t],
                    value: t,
                  }))}
                  block={isMobile}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
